import { get } from 'lodash-es';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { getPageLinksPath, LinkWidgetsModel } from 'client/data/models/link-widgets';
import { pageDefinition } from 'client/site-modules/shared/pages/page';
import { getFAQSchema } from 'client/seo/schemas/faq';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { create404Error } from 'site-modules/shared/utils/errors';
import { getModelState } from 'client/data/luckdragon/model';
import { getResearchArticleBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';
import { EdmundsNoAdsDecorator } from 'site-modules/shared/pages/decorators/edmunds-no-ads';
import { getSellMyCarInCityStateFaqContent } from 'client/site-modules/editorial/utils/sell-car';
import { UrlModel, URL_CONTEXT_PATH } from 'client/data/models/url';
import { CmsModel } from 'client/data/models/cms';
import { StatesModel } from 'client/data/models/states';
import { SELL_CAR_CONTENT_PATH } from 'client/data/constants/contents-paths';
import { setCriticalCss } from 'client/actions/critical-css';
import { getIsEmoSearchEnabled } from 'client/site-modules/shared/utils/multi-offer/multi-offer';
import { getInitialTabIdFromLocation } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/get-initial-tab-id-from-location';
import { REVIEWS_MODULE_USER_REVIEW_CONTENT_PATH } from 'site-modules/shared/components/offers-reviews-module/constants';

import { SellMyCarInCityState } from './sell-my-car-in-city-state';

function runPreload(store, { isPreloadAboveFold = false } = {}) {
  const {
    pageContext: { location, page },
  } = store.getState();
  const preloader = new ModelPreloader(store);

  return preloader.load().then(modelState => {
    const urlContext = modelState.get(URL_CONTEXT_PATH, UrlModel);
    const isValidUrl = get(urlContext, 'isValidUrl', false);

    if (!isValidUrl) {
      throw create404Error();
    }
    preloader.resolve(`content["${SELL_CAR_CONTENT_PATH}"]`, CmsModel);
    preloader.resolve(getPageLinksPath(), LinkWidgetsModel);
    preloader.resolve('allStateCodes', StatesModel);
    if (!isPreloadAboveFold) {
      preloader.resolve(`content["${REVIEWS_MODULE_USER_REVIEW_CONTENT_PATH}"]`, CmsModel);
    }

    const isEmoSearchEnabled = getIsEmoSearchEnabled(location);
    const initialTabId = getInitialTabIdFromLocation({
      location,
      isLicensePlateTabDefault: true,
      isSellCar: true,
      referer: page?.referer,
    });

    store.dispatch(
      setCriticalCss({
        isEmoSearchEnabled, // 2 values
        initialTabId, // 3 values
      })
    );

    return preloader.load();
  });
}

export const pageDefinitionConfiguration = {
  name: 'advice_sell_article_index',
  category: 'advice',
  component: SellMyCarInCityState,
  decorator: EdmundsNoAdsDecorator,
  preload: store => runPreload(store),
  preloadAboveFold: store => runPreload(store, { isPreloadAboveFold: true }),
  seo: (state, props) => {
    const modelState = getModelState(state);
    const urlContext = modelState.get(URL_CONTEXT_PATH, UrlModel);
    const { city, stateName } = get(urlContext, 'location', {});

    const title = `Sell My Car Online for Cash in ${city}, ${stateName}`;
    const pathname = get(props, 'location.pathname', '');

    return {
      title: `${title} | Edmunds`,
      description: `Sell my car, truck, or SUV in ${city} instantly online for cash with Edmunds. Free appraisal, instant no-obligation offer. Check it out. You've got nothing to lose.`,
      jsonld: [
        getFAQSchema({
          entries: getSellMyCarInCityStateFaqContent(city),
          generateLearnMoreLinks: true,
          pathname,
        }),
        getBreadcrumbSchema(
          getResearchArticleBreadcrumbs(['sell-car'], `Sell My Car In ${city}, ${stateName}`, pathname)
        ),
      ],
    };
  },
};

export default pageDefinition(pageDefinitionConfiguration);
